import type {TFunction} from "i18next";
import {Message} from "primereact/message";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import type {ReactNode, RefObject} from "react";
import {useRef} from "react";
import {useTranslation} from "react-i18next";

import type {RevaLanguage} from "@reva/shared/i18n";

import {useRevaTranslation} from "../hooks/languageHooks";
import type {ApiQueryError} from "../redux/types";
import type {CustomErrorData, XErrorData} from "../util/errorUtil";
import {getErrorDescription, getErrorDetail, X_ERROR_KEY, X_ERROR_SEPARATOR} from "../util/errorUtil";

export interface I18nArgs {
    errorData?: XErrorData | CustomErrorData;

    [key: string]: unknown;
}

export interface I18nErrorMessageProps {
    /** Optional enabled-flag, which needs to be truthy in order to render the message */
    enabled?: boolean;
    /** Translation key of error title. If empty, no title will be displayed */
    titleI18nKey?: string | null;
    /** Translation key to render. If empty, no error message will be rendered */
    i18nKey?: string | null;
    /** Optional arguments to the translation key */
    i18nArgs?: I18nArgs | null;
    /** Optional flag, if a contact link should also be displayed */
    hasContactLink?: boolean;
    /** Optional class name for the messages. If missing, the message will be full-width. */
    className?: string;
    /** Optional additional class name for the contact link panel. */
    classNameContactLink?: string;
    /** Instead of parameters enabled, i18nKey, i18nArgs and hasContactLink, an error object can be given */
    errorObject?: ApiQueryError | null;
    /**
     * Optionaler Inhalt zwischen Fehlermeldung und Kontakt-Hinweis, z.B. ein konkreter
     * Lösungsvorschlag. Bewusst VOR dem Kontakt-Hinweis: was der Benutzer selbst tun kann,
     * gehört vor "wenden Sie sich an uns".
     */
    contentAfterMessage?: ReactNode;
}

/**
 * Standardized error message panel for translatable error
 */
export default function I18nErrorMessage({
    enabled,
    titleI18nKey,
    i18nKey,
    i18nArgs,
    hasContactLink,
    className = "w-full",
    classNameContactLink = "",
    errorObject,
    contentAfterMessage,
}: I18nErrorMessageProps) {
    const {t, language} = useRevaTranslation();
    const messageKey = errorObject?.error || i18nKey;
    const messageArgs = (errorObject?.errorArgs as I18nArgs | null) || i18nArgs;
    const showContact = !!(errorObject?.hasContactLink || hasContactLink);

    if (!messageKey || (typeof enabled !== "undefined" && !enabled)) {
        // No error key was set or a falsy enabled-flag given
        return null;
    }

    const messageText = computeErrorText(t, language, {i18nKey: messageKey, i18nArgs: messageArgs});

    let messageNode: ReactNode;
    if (titleI18nKey)
        messageNode = <TitledErrorMessage title={t(titleI18nKey)} text={messageText} className={className} />;
    else if ((messageArgs?.errorData as unknown as Record<string, unknown>)?.[X_ERROR_KEY] != null)
        messageNode = (
            <ErrorMessageWithTooltip
                i18nKey={messageKey}
                i18nArgs={
                    messageArgs as I18nArgs & {
                        errorData: XErrorData | CustomErrorData;
                    }
                }
                className={className}
            />
        );
    else messageNode = <Message severity="error" text={messageText} className={className} />;

    return (
        <>
            {messageNode}
            {contentAfterMessage}
            {showContact && <ErrorContactInfo className={className} classNameContactLink={classNameContactLink} />}
        </>
    );
}

/**
 * Function to compute i18n error text from an error object or explicit error keys.
 * @param t translation function
 * @param language current UI language
 * @param args error text options
 * @param args.errorObject error object
 * @param args.i18nKey translation key to render, instead of error object
 * @param args.i18nArgs optional arguments to the translation key, instead of error object
 */
export function computeErrorText(
    t: TFunction,
    language: RevaLanguage,
    args: {
        errorObject?: ApiQueryError | null;
        i18nKey?: string | null;
        i18nArgs?: I18nArgs | null;
    },
): string {
    const {errorObject, i18nKey, i18nArgs} = args;
    const messageKey = errorObject?.error ?? i18nKey;
    const messageArgs = (errorObject?.errorArgs as I18nArgs | null) ?? i18nArgs;
    const errorData = messageArgs?.errorData;
    if ((errorData as unknown as Record<string, unknown>)?.["error"]) {
        const fallback = t(messageKey ?? "");
        const errorDescription = getErrorDescription(errorData!, language) ?? fallback;
        const errorDetail = getErrorDetail(errorData!, language);
        return `${errorDescription}: ${errorDetail}`;
    }
    return t(messageKey ?? "", messageArgs ?? undefined);
}

interface ErrorContactInfoProps {
    /** Class name for the messages */
    className?: string;
    /** Additional class name for the contact link panel */
    classNameContactLink?: string;
}

export function ErrorContactInfo({className = "w-full", classNameContactLink = ""}: ErrorContactInfoProps) {
    const {t} = useTranslation();
    return (
        <Message
            severity="warn"
            className={`${className} ${classNameContactLink} mt-2`}
            content={
                <span>
                    {t("general.contactOnError")}
                    <a
                        href="https://www.value-marktdaten.de/contact"
                        target="_blank"
                        rel="noreferrer"
                        style={{color: "inherit"}}
                        className="p-button-label no-underline ml-2"
                    >
                        {t("general.contact")}
                    </a>
                </span>
            }
        />
    );
}

interface TitledErrorMessageProps {
    /** Text of error message */
    text: string;
    /** Title of error message */
    title: string;
    /** Class name for the messages */
    className: string;
    children?: ReactNode;
}

/**
 * Titled error message panel for translatable error
 */
export function TitledErrorMessage({text, title, className, children}: TitledErrorMessageProps) {
    return (
        <div className={`p-inline-message p-component p-inline-message-error block mb-4 ${className}`}>
            <div className="mb-4 text-2xl">
                <span className="pi pi-times-circle text-2xl mr-4"></span>
                {title}
            </div>
            <div>{text}</div>
            {children}
        </div>
    );
}

interface ErrorMessageWithTooltipProps {
    /** Translation key to render */
    i18nKey: string;
    /** Detailed error data */
    i18nArgs: I18nArgs & {errorData: XErrorData | CustomErrorData};
    /** Class name for the messages */
    className: string;
}

/**
 * Error message panel for custom error with details in tooltip
 */
function ErrorMessageWithTooltip({i18nKey, i18nArgs, className}: ErrorMessageWithTooltipProps) {
    const {t, language} = useRevaTranslation();
    const toastRef = useRef<Toast | null>(null);
    const messageRef = useRef<HTMLDivElement>(null);
    const i18nArgsEffective = {errorMessage: getErrorDescription(i18nArgs.errorData, language)};
    const errorDetails = getErrorDetail(i18nArgs.errorData, language);

    const onErrorDetailsCopy = async () => {
        try {
            await navigator.clipboard.writeText(errorDetails.split(X_ERROR_SEPARATOR).join(""));
            toastRef.current?.show({
                severity: "info",
                summary: "Info",
                detail: t("object.copiedErrorDetailsToClipboard"),
            });
        } catch (e) {
            console.warn("Could not copy error details to clipboard", e);
        }
    };

    return (
        <>
            <div ref={messageRef}>
                <Message
                    severity="error"
                    text={t(i18nKey, i18nArgsEffective)}
                    className={className}
                    onClick={() => void onErrorDetailsCopy()}
                />
            </div>
            <Tooltip target={messageRef as RefObject<HTMLElement>} className="custom-tooltip p-in" position={"top"}>
                <ul>
                    {errorDetails.split(X_ERROR_SEPARATOR).map((text, index) => (
                        <li key={index}>{text}</li>
                    ))}
                </ul>
                <div>
                    <i>{t("object.actions.copyToClipboard")}</i>
                </div>
            </Tooltip>
            <Toast ref={toastRef} position="top-center" />
        </>
    );
}
